import React, { useState } from 'react';
import { X, User, Mail, Phone, MapPin, Building, Briefcase, FileText, Activity, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import NotesTab from './shared/NotesTab';
import ActivityTab from './shared/ActivityTab';

const ContactDetailModal = ({ contact, isOpen, onClose, onEdit, canEdit = false }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [currentUser, setCurrentUser] = useState(null);

  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  if (!contact) return null;

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Passive': 'bg-blue-100 text-blue-800',
      'DNC': 'bg-red-100 text-red-800',
      'Archive': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="contact-detail-modal">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <User className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-foreground" data-testid="contact-name-header">
                {fullName}
              </h2>
            </div>
            {contact.title && (
              <p className="text-sm text-muted-foreground">{contact.title}</p>
            )}
            {(contact.customer_name || contact.vendor_name) && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Building className="h-3 w-3" />
                {contact.customer_name || contact.vendor_name}
              </p>
            )}
          </div>
          {contact.status && (
            <Badge className={getStatusColor(contact.status)} data-testid="contact-status-badge">
              {contact.status}
            </Badge>
          )}
        </div>

        <Separator className="mb-4" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mb-4" data-testid="contact-detail-tabs">
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
              <MessageSquare className="h-4 w-4" />
              Notes
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6 mt-0" data-testid="details-content">
              {/* Contact Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contact.primary_email && (
                    <div className="flex items-start gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <a href={`mailto:${contact.primary_email}`} className="ml-1 font-medium text-blue-600 hover:underline">
                          {contact.primary_email}
                        </a>
                      </div>
                    </div>
                  )}
                  {contact.secondary_email && (
                    <div className="flex items-start gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Secondary Email:</span>
                        <a href={`mailto:${contact.secondary_email}`} className="ml-1 font-medium text-blue-600 hover:underline">
                          {contact.secondary_email}
                        </a>
                      </div>
                    </div>
                  )}
                  {contact.client_direct_phone?.number && (
                    <div className="flex items-start gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Phone:</span>
                        <span className="ml-1 font-medium">{contact.client_direct_phone.number}</span>
                      </div>
                    </div>
                  )}
                  {contact.mobile_phone?.number && (
                    <div className="flex items-start gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Mobile:</span>
                        <span className="ml-1 font-medium">{contact.mobile_phone.number}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Professional Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90">Professional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contact.title && (
                    <div className="flex items-start gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Title:</span>
                        <span className="ml-1 font-medium">{contact.title}</span>
                      </div>
                    </div>
                  )}
                  {contact.department && (
                    <div className="flex items-start gap-2 text-sm">
                      <Building className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Department:</span>
                        <span className="ml-1 font-medium">{contact.department}</span>
                      </div>
                    </div>
                  )}
                  {contact.owner && (
                    <div className="flex items-start gap-2 text-sm">
                      <User className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Owner:</span>
                        <span className="ml-1 font-medium">{contact.owner}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              {contact.address && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Address</h3>
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        {contact.address.line1 && <div>{contact.address.line1}</div>}
                        {contact.address.line2 && <div>{contact.address.line2}</div>}
                        {contact.address.city && (
                          <div>
                            {contact.address.city}
                            {contact.address.state && `, ${contact.address.state}`}
                            {contact.address.zip && ` ${contact.address.zip}`}
                          </div>
                        )}
                        {contact.address.country && <div>{contact.address.country}</div>}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Comments */}
              {contact.general_contact_comments && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Comments</h3>
                    <p className="text-sm text-foreground/80 whitespace-pre-wrap">{contact.general_contact_comments}</p>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-0" data-testid="activity-content">
              <ActivityTab entityType="contact" entityId={contact?.id} />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4 mt-0" data-testid="notes-content">
              <NotesTab entityType="contact" entityId={contact?.id} currentUser={currentUser} />
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} data-testid="close-modal-btn">
            Close
          </Button>
          {onEdit && canEdit && (
            <Button variant="default" onClick={() => onEdit(contact)} data-testid="edit-contact-btn">
              Edit Contact
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailModal;
